import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Checkbox, Radio } from "@mantine/core";
import { Link } from "react-router-dom";
import { callFunction } from "../api/client";
import type { Campaign } from "../api/types";
import { markAsked } from "../lib/askedFiles";
import { FILES, campaignPath, type FileKey } from "../lib/campaign";
import { shortDate } from "../lib/format";
import { Dialog } from "../ui/Dialog";

/* Export: one action, two questions, and the file lands on the page.
 *
 * It replaces three buttons that each queued a job and sent the file to a
 * table further down. Which file, and which rows: everything on the list, or
 * only what no export has delivered, with the counts before the press. For
 * an Orum list from a Ready to call campaign, a checkbox, on by default,
 * marks the campaign Calling once the file is made, because loading the list
 * into Orum is when calling starts. The engine makes that move when the file
 * is made (p_mark_calling, engine migration 293), and the file's line is
 * CampaignFile's. docs/plans/campaigns-overhaul.md, The export.
 *
 * The file is the list as it is, with the people above the campaign's line
 * at each property (engine docs/campaign-people.md). The engine never
 * updates the list on its own for an export: "Update the list first", off by
 * default, does that before the file is made, and the line says how many
 * people the file carries and when the list was taken.
 *
 * When another owner has sent out some of this list's people to call on an
 * open campaign, a checkbox, off by default, leaves those people out of the
 * file and lists the next person where a property has one. Nothing is left
 * out by a rule, because which rep calls is the sales team's decision. The
 * engine counts who it left out as it makes the file, and the file's line
 * says so (engine migrations 217 and 298, docs/plans/campaign-assignment.md
 * milestone 12). */

/** api.campaign_export_count: the people to call, and how many each file's
 *  rule keeps. */
type ExportCount = {
  people: number; with_phone: number; with_email_or_phone: number;
  without_phone_or_email: number; other_owners: number; taken: boolean;
};

/** The line under the file choice: how many people this file will hold,
 *  by its own rule, out of the people to call. */
export function fileCountWords(file: FileKey, c: ExportCount): string {
  const people = c.people.toLocaleString();
  if (file === "orum") return `${c.with_phone.toLocaleString()} of ${people} people to call have a phone and go on the Orum list.`;
  if (file === "crm") return `${c.with_email_or_phone.toLocaleString()} of ${people} people to call have an email or a phone and go in the CRM files.`;
  return `All ${people} people to call go on the Lead Sheet, ${c.without_phone_or_email.toLocaleString()} of them with no phone or email yet.`;
}

export function ExportDialog({ token, c, opened, onClose, onQueued, newOnly }: {
  token: string;
  c: Campaign;
  opened: boolean;
  onClose: () => void;
  /** The export's job id, once it is queued. */
  onQueued: (jobId: string) => void;
  /** Open with "new since the last export" chosen, from Export new rows. */
  newOnly?: boolean;
}) {
  const name = c.name ?? "";
  const exported = (c.export_count ?? 0) > 0;
  const [file, setFile] = useState<FileKey>("orum");
  const [rows, setRows] = useState<"all" | "new">(newOnly && exported ? "new" : "all");
  const [markCalling, setMarkCalling] = useState(true);
  const [leaveOut, setLeaveOut] = useState(false);
  /* Off by default. James, 2026-10-13: an export never updates the list on
     its own. It asks, and the file is the list as it is unless this is on. */
  const [refresh, setRefresh] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setRows(newOnly && exported ? "new" : "all");
    setLeaveOut(false);
    /* Ticked when the criteria changed after the list was made, so an old
       list is not exported without a choice (engine migration 296). */
    setRefresh(Boolean(c.list_behind));
    setError(null);
    setBusy(false);
  }, [opened, newOnly, exported, c.list_behind]);

  /* Who is on the file, by the file's own rule: the people above the
     campaign's line, counted once each from its rows, with a phone for the
     Orum list, with an email or a phone for the CRM files, and everyone for
     the Lead Sheet (api.campaign_export_count, engine migration 239).
     Counted as the file counts them when people another owner has sent
     out are left out. */
  const onFile = useQuery({
    queryKey: ["campaign-export-count", name, leaveOut],
    enabled: opened,
    queryFn: () => callFunction<ExportCount>(token, "campaign_export_count",
      { p_name: name, p_leave_out_other_owners: leaveOut }),
  });

  /* How many of the people to call another owner has sent out on an open
     campaign, who the Leave out box takes out (engine migration 298). Read
     with the box off, the same query as the line above until it is ticked,
     so ticking it does not hide it while the count loads again. */
  const others = useQuery({
    queryKey: ["campaign-export-count", name, false],
    enabled: opened,
    queryFn: () => callFunction<ExportCount>(token, "campaign_export_count",
      { p_name: name, p_leave_out_other_owners: false }),
  });
  const otherOwners = others.data?.other_owners ?? 0;

  /* The stages this campaign's criteria can return, and which of them have
     no call note angle, for the one line under the Orum choice. */
  const stages = useQuery({
    queryKey: ["call-angle-stages", JSON.stringify(c.filters ?? {})],
    enabled: opened,
    queryFn: () => callFunction<string[] | null>(token, "call_angle_stages", { p_filters: c.filters ?? {} }),
  });
  const angles = ((c.shape ?? {}) as { call_angles?: Record<string, string> }).call_angles ?? {};
  const missing = (stages.data ?? []).filter((s) => !(angles[s] ?? "").trim());
  const offerCalling = file === "orum" && c.stage === "ready_to_call";

  const run = async () => {
    setBusy(true); setError(null);
    try {
      const args: Record<string, unknown> = { p_criteria_sets: [name] };
      if (rows === "new") args.p_since = "last";
      if (leaveOut) args.p_leave_out_other_owners = true;
      if (refresh) args.p_refresh = true;
      if (offerCalling && markCalling) args.p_mark_calling = true;
      const id = await callFunction<string>(token, FILES[file].fn, args);
      // This tab asked for it, so it downloads here by itself when made.
      markAsked(id);
      onQueued(id);
      onClose();
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Dialog opened={opened} onClose={onClose} title={`Export ${c.label ?? name}`} busy={busy} error={error}
            action={{ label: "Export", onPress: run }}>
      <Radio.Group label="File" value={file} onChange={(v) => setFile(v as FileKey)} className="cx-group">
        {(Object.keys(FILES) as FileKey[]).map((k) => (
          <Radio key={k} value={k} label={FILES[k].word} description={FILES[k].says} className="cx-choice" />
        ))}
      </Radio.Group>

      <Radio.Group label="Rows" value={rows} onChange={(v) => setRows(v as "all" | "new")} className="cx-group">
        <Radio value="all" className="cx-choice"
               label={<>Everything <span className="muted">{(c.list_count ?? c.properties ?? 0).toLocaleString()} properties</span></>} />
        {exported ? (
          <Radio value="new" className="cx-choice"
                 label={<>New since the last export <span className="muted">
                   {c.not_yet_delivered === null || c.not_yet_delivered === undefined
                     ? "not measured yet" : `${c.not_yet_delivered.toLocaleString()} properties`}, since {shortDate(c.last_export_at)}
                 </span></>} />
        ) : null}
      </Radio.Group>

      <p className="muted cx-note">
        {onFile.isError ? <span className="refusal">{(onFile.error as Error).message}</span>
          : onFile.data && !onFile.data.taken ? <span className="refusal">No people chosen for this list yet. Update the list first.</span>
          : onFile.data ? fileCountWords(file, onFile.data)
          : null}
        {" "}List as of {shortDate(c.list_at)}.
      </p>
      <Checkbox checked={refresh} onChange={(e) => setRefresh(e.currentTarget.checked)} className="cx-group"
                label="Update the list first"
                description="Runs the campaign's search again and takes its people, then makes the file from the new list." />
      {c.list_behind && c.list_behind_words ? <p className="muted cx-note">{c.list_behind_words}</p> : null}

      {offerCalling ? (
        <Checkbox checked={markCalling} onChange={(e) => setMarkCalling(e.currentTarget.checked)}
                  label="Mark the campaign Calling" className="cx-group"
                  description={c.when_calling_words ?? undefined} />
      ) : null}

      {otherOwners > 0 ? (
        <Checkbox checked={leaveOut} onChange={(e) => setLeaveOut(e.currentTarget.checked)} className="cx-group"
                  label="Leave out people another owner has sent out"
                  description={`${otherOwners.toLocaleString()} of the people to call ${otherOwners === 1 ? "was" : "were"} sent out on another owner's campaign.`} />
      ) : null}

      {file === "orum" && missing.length ? (
        <p className="muted cx-note">
          {missing.length} of {(stages.data ?? []).length} stages have no call note angle. The note prints without one.{" "}
          <Link to={campaignPath(name, "notes")} onClick={onClose}>Call notes</Link>
        </p>
      ) : null}
    </Dialog>
  );
}
